import { ENDPOINTS, EXPORT_FILE_FORMATS, sendAsyncRequest } from '../utils';
import { logger } from '../utils/logger';
import { ServiceDeliveryResponse, ServiceDeliveryParams, ServiceDeliveryCreateData, ServiceDeliveryUpdateData, ServiceDeliveryStatusUpdateData, PaginatedResponse, ApiError, ApiErrorCode } from '../types/api.types';

/**
 * Extract structured error from axios error response
 */
const extractApiError = (error: unknown): ApiError => {
  if (error && typeof error === 'object') {
    const axiosError = error as {
      response?: {
        data?: {
          error?: {
            code?: ApiErrorCode;
            message?: string;
            field?: string;
            details?: Record<string, unknown>;
          };
          message?: string;
          code?: ApiErrorCode;
          field?: string;
        };
        status?: number;
      };
      message?: string;
    };

    const responseData = axiosError.response?.data;
    const errorData = responseData?.error || responseData;
    const statusCode = axiosError.response?.status;

    if (errorData) {
      return new ApiError(
        errorData.message || axiosError.message || 'Unknown error',
        errorData.code,
        errorData.field,
        statusCode,
        errorData.details
      );
    }

    return new ApiError(
      axiosError.message || 'Unknown error',
      undefined,
      undefined,
      statusCode
    );
  }

  if (error instanceof Error) {
    return new ApiError(error.message);
  }

  return new ApiError('Unknown error');
};

const getServiceDeliveryList = async (): Promise<ServiceDeliveryResponse> => {
  const response = await sendAsyncRequest<ServiceDeliveryResponse>('get', ENDPOINTS.SERVICEDELIVERIES, true, undefined, undefined);
  return response.data;
};

const getServiceDeliveryBYID = async (id: string, page?: number, perPage?: number): Promise<ServiceDeliveryResponse> => {
  const response = await sendAsyncRequest<ServiceDeliveryResponse>(
    'get',
    ENDPOINTS.SERVICEDELIVERIES + '/' + id,
    true,
    { page: page, limit: perPage, filter: { include: [{ relation: 'types' }] } },
    undefined,
  );
  return response.data;
};

const getServiceDeliveryBYPAT = async (pat_ids: string[]): Promise<ServiceDeliveryResponse> => {
  const response = await sendAsyncRequest<ServiceDeliveryResponse>(
    'get',
    ENDPOINTS.SERVICEDELIVERIES ,
    true,
    { pat_ids: pat_ids },
    undefined,
  );
  return response.data;
};

const getServiceJob = async (params: ServiceDeliveryParams, url: string): Promise<ServiceDeliveryResponse> => {
  const weekCalendarResponse = await sendAsyncRequest<ServiceDeliveryResponse>('get', url, true, params, undefined);
  return weekCalendarResponse.data;
};
const getServiceDeliveryListGTI = async (page?: number, perPage?: number): Promise<PaginatedResponse<ServiceDeliveryResponse>> => {
  const response = await sendAsyncRequest<PaginatedResponse<ServiceDeliveryResponse>>('get', ENDPOINTS.SERVICEDELIVERIESGTI, true, { page: page, limit: perPage }, undefined);
  return response.data;
};

const addNewServiceDelivery = async (data: ServiceDeliveryCreateData): Promise<ServiceDeliveryResponse> => {
  try {
    const response = await sendAsyncRequest('post', ENDPOINTS.SERVICEDELIVERIES, true, undefined, data);
    return response.data;
  } catch (error) {
    logger.error('ServiceDeliveryAPI', 'Error in addNewServiceDelivery', { error: error.message });
    throw extractApiError(error);
  }
};

const editServiceDel = async (
  id: string,
  data: ServiceDeliveryUpdateData,
): Promise<ServiceDeliveryResponse> => {
  const response = await sendAsyncRequest<ServiceDeliveryResponse>('put', ENDPOINTS.SERVICEDELIVERIES + '/' + id, true, undefined, data);
  return response.data;
};
const updateServiceDelStatus = async (
  id: string,
  data: ServiceDeliveryStatusUpdateData,
): Promise<ServiceDeliveryResponse> => {
  const response = await sendAsyncRequest('patch', ENDPOINTS.SERVICEDELIVERIES + '/' + id + ENDPOINTS.UPDATE_STATUS, true, undefined, data);
  return response.data;
};

const exportServiceDelivery = (exportType: string, startDate: string, endDate: string) => {
  // S'assurer que l'URL commence par /
  const exportPath = ENDPOINTS.EXPORT.startsWith('/') ? ENDPOINTS.EXPORT : `/${ENDPOINTS.EXPORT}`;
  const apiURL = `${exportPath}${ENDPOINTS.EXPORT_TYPE}${exportType}${ENDPOINTS.STARTDATE}${startDate}${ENDPOINTS.ENDDATE}${endDate}`;
  
  logger.debug('ServiceDeliveryAPI', 'Export service delivery', { exportType, startDate, endDate, apiURL });
  
  return sendAsyncRequest('get', apiURL, true, undefined, undefined, 'arraybuffer')
    .then((response) => {
      if (!response || !response.data) {
        throw new Error('No data received from server');
      }
      let binaryData = [];
      binaryData.push(response.data);
      const _URL = window.URL || window.webkitURL;
      
      // Déterminer le type MIME selon le format
      let mimeType = 'application/octet-stream';
      const format = EXPORT_FILE_FORMATS.find((e) => e.format === exportType || e.format.toLowerCase() === exportType.toLowerCase());
      if (format) {
        if (format.extension === '.pdf') {
          mimeType = 'application/pdf';
        } else if (format.extension === '.csv') {
          mimeType = 'text/csv';
        } else if (format.extension === '.xlsx') {
          mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        }
      }
      
      const downloadUrl = _URL.createObjectURL(new Blob(binaryData, { type: mimeType }));
      const link = document.createElement('a');
      link.href = downloadUrl;
      const extension = format?.extension || '.pdf';
      link.setAttribute('download', `interventions_${Date.now()}${extension}`);
      document.body.appendChild(link);
      link.click();
      // Nettoyer l'URL après le téléchargement
      setTimeout(() => {
        _URL.revokeObjectURL(downloadUrl);
        link.remove();
      }, 100);
    })
    .catch((error) => {
      logger.error('ServiceDeliveryAPI', 'Export error', { details: error.response?.data || error.message });
      throw error;
    });
};

const purgeServiceDelivery = async (startDate: string): Promise<ServiceDeliveryResponse> => {
  const response = await sendAsyncRequest<ServiceDeliveryResponse>('delete', ENDPOINTS.SERVICEDELIVERIES + '/purge', true, { end_date: startDate }, undefined);
  return response.data;
};

const deleteServiceDelivery = async (id: string): Promise<ServiceDeliveryResponse> => {
  const response = await sendAsyncRequest<ServiceDeliveryResponse>('delete', ENDPOINTS.SERVICEDELIVERIES + '/' + id, true, undefined, undefined);
  return response.data;
};

const validateWeek = async (collaboratorId: string, weekDate: string): Promise<{ count: number }> => {
  const response = await sendAsyncRequest<{ count: number }>(
    'patch',
    ENDPOINTS.SERVICEDELIVERIES + ENDPOINTS.VALIDATE_WEEK,
    true,
    undefined,
    { collaboratorId, weekDate },
  );
  return response.data;
};

const addIntervention = async (data: ServiceDeliveryCreateData): Promise<ServiceDeliveryResponse> => {
  try {
    const response = await sendAsyncRequest<ServiceDeliveryResponse>('post', ENDPOINTS.SERVICEDELIVERIES, true, undefined, data);
    return response.data;
  } catch (error) {
    logger.error('ServiceDeliveryAPI', 'Error in addIntervention', { error: error.message });
    throw extractApiError(error);
  }
};

export const serviceDelivery = {
  getServiceDeliveryList,
  getServiceDeliveryBYID,
  addNewServiceDelivery,
  editServiceDel,
  exportServiceDelivery,
  purgeServiceDelivery,
  deleteServiceDelivery,
  getServiceDeliveryListGTI,
  addIntervention,
  getServiceJob,
  getServiceDeliveryBYPAT,
  updateServiceDelStatus,
  validateWeek,
};
